import { APP_INITIALIZER, DEFAULT_CURRENCY_CODE, LOCALE_ID, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { ActivatedRoute, Router, RouteReuseStrategy } from '@angular/router';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';

// LANGUAGE RELATED
import localeAr from '@angular/common/locales/ar';
import localeEn from '@angular/common/locales/en';
import localeEt from '@angular/common/locales/et';
import localeFi from '@angular/common/locales/fi';
import localeLt from '@angular/common/locales/lt';
import localeLv from '@angular/common/locales/lv';
import localeRu from '@angular/common/locales/ru';
import localeSv from '@angular/common/locales/sv';
import localeKa from '@angular/common/locales/ka';

// LANGUAGE RELATED
registerLocaleData(localeEn);
registerLocaleData(localeKa);
registerLocaleData(localeRu);
registerLocaleData(localeAr);
registerLocaleData(localeEt);
registerLocaleData(localeFi);
registerLocaleData(localeLt);
registerLocaleData(localeLv);
registerLocaleData(localeSv);
registerLocaleData(localeKa);

import { NgbModule, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';

import { IonicModule, IonicRouteStrategy } from '@ionic/angular';

import { AuthInterceptor } from './interceptors/auth.interceptor';

import { AppComponent } from './app.component';
import { AppRoutingModule } from './app-routing.module';
import { SigninComponent } from './components/public/signin/signin.component';
import { HomeComponent} from './components/home/home.component';
import { ShiftsComponent } from './components/shifts/shifts.component';
import { EditComponent } from './components/shifts/edit/edit.component';
import { FeedbackComponent } from './components/shifts/edit/feedback/feedback.component';
import { MessagesComponent } from './components/messages/messages.component';
import { CustomerComponent } from './components/customer.component';
import { PasswordComponent } from './components/password/password.component';
import { CheckoutComponent } from './components/checkout/checkout.component';
import { AuthImgPipe } from './pipes/auth-img.pipe';
import { ForgotPaswordComponent } from './components/public/forgot-pasword/forgot-pasword.component';
import { SettingsComponent } from './components/settings/settings.component';
import { LocationComponent } from './components/settings/location/location.component';
import { registerLocaleData } from '@angular/common';
import { NotesComponent } from './components/shifts/edit/notes/notes.component';
import { ChangeComponent } from './components/shifts/edit/change/change.component';
import { ProductAddComponent } from './components/shifts/edit/product-add/product-add.component';
import { TranslateFieldPipe } from './pipes/translate-field.pipe';
import { LocaleSortPipe } from './pipes/locale-sort.pipe';
import { TncComponent } from './components/common/tnc/tnc.component';
import { PaymentsComponent } from './components/payments/payments.component';
import { PayBalanceComponent } from './components/settings/pay-balance/pay-balance.component';
import { EditCardComponent } from './components/settings/edit-card/edit-card.component';
import { PaymentHistoryComponent } from './components/settings/payment-history/payment-history.component';
import { LocationComponent as ReportLocationComponent } from './components/reports/location/location.component';
import { PublicComponent } from './components/public/public.component';
//import { OrderComponent } from './components/public/order.old/order.component';
import { AboutComponent } from './components/public/about/about.component';
import { LanguageSelectorComponent } from './components/common/language-selector/language-selector.component';
import { AssignmentComponent } from './components/public/about/assignment/assignment.component';
import { NgbTypeaheadModule } from '@ng-bootstrap/ng-bootstrap';
import { LocationComponent as NewShiftLocationComponent } from './components/public/order/schedule/location/location.component';
import { FeedbackComponent as OrderFeedbackCoponent} from './components/public/order/schedule/feedback/feedback.component';
import { CancelComponent } from './components/shifts/edit/cancel/cancel.component';
import { DetailsComponent } from './components/shifts/edit/details/details.component';
import { ReferralsComponent } from './components/settings/referrals/referrals.component';
import { DiscountsComponent } from './components/settings/discounts/discounts.component';
import { ContenteditableValueAccessorModule } from '@tinkoff/angular-contenteditable-accessor';
import { PartnersComponent } from './components/public/partners/partners.component';
import { PersonalComponent } from './components/settings/personal/personal.component';
import { PrivacyPolicyComponent } from './components/common/privacy-policy/privacy-policy.component';
import { PrivacyComponent } from './components/public/privacy/privacy.component';
import { ProductComponent } from './components/public/order/services/product/product.component';
import { SalaryRulesComponent } from './components/public/order/services/salary-rules/salary-rules.component';
import { PressComponent } from './components/public/press/press.component';
import { AnnouncementComponent } from './components/common/announcement/announcement.component';
import { AuthService } from './services/auth.service';
import { TncComponent as TermsAndConditionsComponent } from './components/public/tnc/tnc.component';
import { RequireComponent } from './components/require/require.component';
import { PasswordChangeComponent } from './components/require/password-change/password-change.component';
import { EmailVerificationComponent } from './components/require/email-verification/email-verification.component';
import { DetailsComponent as RequireDetailsComponent } from './components/require/details/details.component';
import { TncComponent as RequireTncComponent } from './components/require/tnc/tnc.component';

import { OrderComponent } from './components/public/order/order.component';
import { ScheduleComponent } from './components/public/order/schedule/schedule.component';
import { ServicesComponent } from './components/public/order/services/services.component';
import { CategoriesComponent } from './components/public/order/categories/categories.component';
import { Location } from '@angular/common';
import { MessagesComponent as ShiftMessagesComponent } from './components/shifts/edit/messages/messages.component';
import { AddressAutocompleteComponent } from './components/common/address-autocomplete/address-autocomplete.component';
import { DirectivesModule } from './directives/directives.module';

export function initializeApp(location: Location, router: Router, authService: AuthService) {
  
  function urlSearchParamsToObject(params: URLSearchParams): any {
    const obj: any = {};
    params.forEach((value, key) => {
      obj[key] = value;
    });
    return obj;
  }

  return () => new Promise<void>((resolve) => {
    const url = location.path(true);
    const queryString = url.split('?')[1] || '';
    const queryParams = new URLSearchParams(queryString);

    let needsNavigation = false;

    // Handle 'r'
    if (queryParams.has('r')) {
      localStorage.setItem('customer_referral_code', queryParams.get('r')!);
      queryParams.delete('r');
      needsNavigation = true;
    }

    // Handle 'authToken'
    if (queryParams.has('authToken')) {
      localStorage.setItem('customer_access_token', queryParams.get('authToken')!);
      queryParams.delete('authToken');
      needsNavigation = true;
    }

    // Handle 'country'
    if (queryParams.has('country')) {
      const country = queryParams.get('country')!;
      queryParams.delete('country');
      needsNavigation = true;

      authService.organizationByCountryCode(country).subscribe({
        next: (result) => {
          localStorage.setItem('organization', result.id);
          localStorage.setItem('api_url', result.api_url);
          localStorage.setItem('ws_url', result.ws_url);
          localStorage.removeItem('order');

          const remainingParams = urlSearchParamsToObject(queryParams);

          router.navigate([], {
            queryParams: remainingParams,
            replaceUrl: true
          }).then(() => { resolve(); });
        },
        error: () => {
          const remainingParams = urlSearchParamsToObject(queryParams);

          router.navigate([], {
            queryParams: remainingParams,
            replaceUrl: true
          }).then(() => { resolve(); });
        }
      });

      return; // Wait for async operation
    }

    // Fallback to GeoIP if needed
    if (!localStorage.getItem('api_url') || !localStorage.getItem('ws_url')) {
      authService.organizationByGeoIP().subscribe({
        next: (result) => {
          localStorage.setItem('organization', result.id);
          localStorage.setItem('api_url', result.api_url);
          localStorage.setItem('ws_url', result.ws_url);
          localStorage.removeItem('order');

          const remainingParams = urlSearchParamsToObject(queryParams);

          router.navigate([], {
            queryParams: remainingParams,
            replaceUrl: true
          }).then(() => { resolve(); });
        },
        error: () => {
          const remainingParams = urlSearchParamsToObject(queryParams);

          router.navigate([], {
            queryParams: remainingParams,
            replaceUrl: true
          }).then(() => { resolve(); });
        }
      });

      return; // Wait for async operation
    }

    // If we reach here → localStorage already has valid api_url/ws_url
    // Safe to resolve
    resolve();
  });
}

@NgModule({
  declarations: [
    RequireComponent,
    PasswordChangeComponent,
    EmailVerificationComponent,
    RequireTncComponent,
    RequireDetailsComponent,
    TncComponent,
    AppComponent, 
    SigninComponent, 
    CustomerComponent,
    HomeComponent,
    ShiftsComponent,
    MessagesComponent,
    EditComponent,
    FeedbackComponent,
    PasswordComponent,
    CheckoutComponent,
    ForgotPaswordComponent,
    SettingsComponent,
    LocationComponent,
    AuthImgPipe,
    NotesComponent,
    ChangeComponent,
    ProductAddComponent,
    TranslateFieldPipe,
    LocaleSortPipe,
    TncComponent,
    TermsAndConditionsComponent,
    PaymentsComponent,
    PayBalanceComponent,
    EditCardComponent,
    PaymentHistoryComponent,
    ReportLocationComponent,
    PublicComponent,
    OrderComponent,
    CategoriesComponent,
    ScheduleComponent,
    ServicesComponent,
    AboutComponent,
    LanguageSelectorComponent,
    AssignmentComponent,
    NewShiftLocationComponent,
    OrderFeedbackCoponent,
    CancelComponent,
    DetailsComponent,
    ReferralsComponent,
    DiscountsComponent,
    PartnersComponent,
    PersonalComponent,
    ProductComponent,
    PrivacyPolicyComponent,
    PrivacyComponent,
    SalaryRulesComponent,
    PressComponent,
    AnnouncementComponent,
    ShiftMessagesComponent,
    AddressAutocompleteComponent
  ],
  imports: [
    BrowserModule, 
    IonicModule.forRoot(), 
    AppRoutingModule, 
    FormsModule, 
    ReactiveFormsModule, 
    FontAwesomeModule, 
    NgbModule, 
    NgbTooltipModule,
    HttpClientModule,
    NgbTypeaheadModule,
    ContenteditableValueAccessorModule,
    DirectivesModule
  ],
  providers: [
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
    //{ provide: DEFAULT_CURRENCY_CODE, useValue: environment.currency },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeApp,
      deps: [Location, Router, AuthService],
      multi: true
    }
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
